import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { brandName } from '#app/components/branding.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { cleanSkillName } from '#app/utils/scripts.helper.js'
import { momentsAgo } from '#app/utils/dateFormat.js'
import { useOptionalUser } from '#app/utils/user.ts'

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Owner not found', { status: 404 })

	const builds = await prisma.userBuildTemplate.findMany({
		where: { userId: owner.id },
		include: {
			server: true,
			user: { select: { username: true } },
			taggables: { include: { tag: true } },
			_count: { select: { reactions: true } },
			originalBuild: {
				select: {
					id: true,
					slug: true,
					server: { select: { slug: true } },
					user: { select: { username: true, name: true } },
				},
			},
		},
		orderBy: { modifiedAt: 'desc' },
	})

	return json({ owner, builds })
}

export default function UserBuildsRoute() {
	const { owner, builds } = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const isOwner = user?.id === owner.id
	const ownerDisplayName = owner.name ?? owner.username

	return (
		<div className="container mx-auto mb-24 px-4 pt-8 md:mb-48">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link to={`/users/${owner.username}`}>
					<img
						src={getUserImgSrc(owner.image?.id)}
						alt={ownerDisplayName}
						className="h-10 w-10 rounded-full object-cover md:h-12 md:w-12"
					/>
				</Link>
				<div>
					<Link to={`/users/${owner.username}`} className="text-sm text-muted-foreground hover:underline">
						&larr; {ownerDisplayName}
					</Link>
					<h1 className="text-xl font-bold md:text-2xl">Builds ({builds.length})</h1>
				</div>
			</div>

			{/* Builds grid */}
			<div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{builds.length === 0 && (
					<p className="col-span-full text-sm text-muted-foreground">No builds yet.</p>
				)}
				{builds.map((build) => {
					let total = 0
					const skills = build.build?.split(',').map((x) => {
						const parts = x.split('|')
						const value = Number(parts[1]) ?? 0
						total += value
						return { name: parts[0] ?? '', value }
					})
					const isAdopted = !!build.originalBuild

					return (
						<Link
							key={build.id}
							to={`/builds/${build.server.slug}/${build.slug}`}
							className="group rounded-xl border bg-muted/30 p-4 transition-colors hover:bg-muted/50"
						>
							<div className="flex items-center justify-between mb-2">
								<img src={build.server.logo} alt={build.server.name} className="h-6 md:h-8" />
								<div className="flex items-center gap-2">
									{isAdopted && (
										<span className="rounded-full bg-green-500/10 px-2 py-0.5 text-[10px] font-medium text-green-600 dark:text-green-400">
											Adopted
										</span>
									)}
									{build._count.reactions > 0 && (
										<span className="text-xs text-muted-foreground">{build._count.reactions} reactions</span>
									)}
								</div>
							</div>
							<h3 className="font-semibold text-sm group-hover:underline md:text-base truncate">
								{build.name || 'Unnamed build'}
							</h3>
							<p className="text-xs text-muted-foreground mt-1">
								{total} skill pts · {momentsAgo(build.createdAt.toString())} ago
							</p>
							{isAdopted && build.originalBuild?.user && (
								<p className="text-[10px] text-muted-foreground mt-0.5">
									from {build.originalBuild.user.name ?? build.originalBuild.user.username}
								</p>
							)}
							{skills && skills.length > 0 && (
								<div className="mt-2 flex flex-wrap gap-1">
									{skills.sort((a, b) => b.value - a.value).slice(0, 4).map((s) => (
										<span key={s.name} className="rounded bg-muted-foreground/10 px-1.5 py-0.5 text-[10px] text-muted-foreground">
											{cleanSkillName(s.name)} {s.value}
										</span>
									))}
									{skills.length > 4 && (
										<span className="rounded bg-muted-foreground/10 px-1.5 py-0.5 text-[10px] text-muted-foreground">
											+{skills.length - 4} more
										</span>
									)}
								</div>
							)}
							{build.taggables.length > 0 && (
								<div className="mt-2 flex flex-wrap gap-1">
									{build.taggables.slice(0, 3).map((t) => (
										<span key={t.tag.name} className="rounded-full bg-muted-foreground/10 px-2 py-0.5 text-xs">
											{t.tag.name}
										</span>
									))}
								</div>
							)}
						</Link>
					)
				})}
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.owner.name ?? params.username
	const count = data?.builds.length ?? 0
	return [
		{ title: `${displayName}'s Builds | ${brandName}` },
		{ name: 'description', content: `${displayName}'s ${count} build${count !== 1 ? 's' : ''} on ${brandName}` },
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => <p>No user with the username "{params.username}" exists</p>,
			}}
		/>
	)
}
